/**
 * Shared lifecycle vocabulary for the orders list (spec "Order States, Alerts
 * & Notifications" v1.1): stage → group/stepper segment, flag → colours,
 * dwell-time formatting and the legacy-status fallback for rows the backend
 * has not stamped yet.
 */
import type { OrderAttentionFlag, OrderOwnerType, OrderResponse, OrderStage } from '../../../services/OrderService';

export const LEGACY_STAGE: Record<string, OrderStage> = {
  draft: 'received',
  submitted: 'submitted',
  in_progress: 'confirmed',
  pending_assistance: 'awaiting_info',
  error: 'received',
  completed: 'completed',
  cancelled: 'cancelled',
};

export const STAGE_ORDER: OrderStage[] = [
  'received', 'analysing', 'waiting_for_input', 'awaiting_info', 'results_ready', 'in_cart', 'submitted', 'confirmed', 'completed', 'cancelled',
];

export type StepperSegment = 'intake' | 'review' | 'cart' | 'ordered';
export const STEPPER_SEGMENTS: StepperSegment[] = ['intake', 'review', 'cart', 'ordered'];

export const STAGE_SEGMENT: Record<OrderStage, StepperSegment | 'cancelled'> = {
  received: 'intake',
  analysing: 'intake',
  waiting_for_input: 'intake',
  awaiting_info: 'intake',
  results_ready: 'review',
  in_cart: 'cart',
  submitted: 'ordered',
  confirmed: 'ordered',
  completed: 'ordered',
  cancelled: 'cancelled',
};

/** Chip tokens (spec §7; the two under-AA chips use the darker foregrounds). */
export const STAGE_STYLE: Record<OrderStage, string> = {
  received: 'bg-[#E0DEED] text-[#5B5872] border-[#C9C5D8]',
  analysing: 'bg-[#E0DEED] text-[#5B5872] border-[#C9C5D8]',
  waiting_for_input: 'bg-gray-50 text-gray-600 border-gray-200',
  awaiting_info: 'bg-[#FEF3C7] text-[#8A5A00] border-amber-200',
  results_ready: 'bg-[#EDEDFF] text-[#4B48C7] border-[#D6D5FF]',
  in_cart: 'bg-[#EDEDFF] text-[#4B48C7] border-[#D6D5FF]',
  submitted: 'bg-[#E0F2FE] text-[#075985] border-sky-200',
  confirmed: 'bg-[#E0F2FE] text-[#075985] border-sky-200',
  completed: 'bg-[#C7FFE8] text-[#0E6E58] border-emerald-200',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
};

export const STAGE_LABEL: Record<OrderStage, string> = {
  received: 'Received',
  analysing: 'Analysing',
  waiting_for_input: 'Waiting for input',
  awaiting_info: 'Awaiting Info',
  results_ready: 'Results Ready',
  in_cart: 'In Cart',
  submitted: 'Submitted',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const ATTENTION_LABEL: Record<OrderAttentionFlag, string> = {
  needs_you: 'Needs you',
  needs_customer: 'Needs customer',
  waiting_customer: 'Waiting on customer',
  system_error: 'System error',
};

/** rail / strip / tint / foreground per flag (spec §2.3, §7). */
export const ATTENTION_TOKENS: Record<OrderAttentionFlag, { rail: string; strip: string; tint: string; fg: string; hover: string }> = {
  needs_you: { rail: '#F59E0B', strip: '#FEF3C7', tint: '#FEF9EC', fg: '#8A5A00', hover: '#FDF3D9' },
  needs_customer: { rail: '#EA580C', strip: '#FFEDD5', tint: '#FFF7F0', fg: '#9A3412', hover: '#FFEFE0' },
  waiting_customer: { rail: '#C9C2FF', strip: '#E5E2FF', tint: '#F7F6FF', fg: '#5B5872', hover: '#F0EEFF' },
  system_error: { rail: '#EF4444', strip: '#FEE2E2', tint: '#FEF4F3', fg: '#B42318', hover: '#FDE8E6' },
};

export const ATTENTION_CHIP: Record<OrderAttentionFlag, string> = {
  needs_you: 'bg-[#FEF3C7] text-[#8A5A00] border-amber-200',
  needs_customer: 'bg-[#FFEDD5] text-[#9A3412] border-orange-200',
  waiting_customer: 'bg-[#E5E2FF] text-[#5B5872] border-[#C9C2FF]',
  system_error: 'bg-[#FEE2E2] text-[#B42318] border-red-200',
};

export const NEEDS_ACTION_FLAGS: OrderAttentionFlag[] = ['needs_you', 'needs_customer', 'system_error'];

export const stageOf = (order: OrderResponse): OrderStage =>
  (order.stage as OrderStage) || LEGACY_STAGE[order.status] || 'received';

export const ownerTypeOf = (order: OrderResponse): OrderOwnerType =>
  order.owner_type || (order.status === 'pending_assistance' ? 'unassigned' : 'agent');

export const flagOf = (order: OrderResponse): OrderAttentionFlag | null => order.attention_flag || null;

export const isUnowned = (order: OrderResponse) => ownerTypeOf(order) === 'unassigned';

/** Row background + left rail keyed on the flag — wins over the legacy amber. */
export const lifecycleRowClass = (order: OrderResponse): string => {
  switch (order.attention_flag) {
    case 'needs_you': return 'bg-[#FEF9EC] hover:bg-[#FDF3D9] border-l-4 border-l-[#F59E0B]';
    case 'needs_customer': return 'bg-[#FFF7F0] hover:bg-[#FFEFE0] border-l-4 border-l-[#EA580C]';
    case 'waiting_customer': return 'bg-[#F7F6FF] hover:bg-[#F0EEFF] border-l-4 border-l-[#C9C2FF] opacity-90';
    case 'system_error': return 'bg-[#FEF4F3] hover:bg-[#FDE8E6] border-l-4 border-l-[#EF4444]';
    default: return '';
  }
};

/** SLA thresholds (hours) per stage — dwell turns amber past them. */
export const STAGE_SLA_HOURS: Partial<Record<OrderStage, number>> = {
  received: 0.25,
  analysing: 0.5,
  results_ready: 4,
  in_cart: 24,
  awaiting_info: 48,
  submitted: 72,
};

export const hoursSince = (iso?: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, (Date.now() - t) / 36e5);
};

/** "4h", "12 min", "3d" — compact dwell. */
export const formatDwell = (hours: number | null, t: (k: string, o?: any) => string): string => {
  if (hours === null) return '';
  if (hours < 1) return t('orders.dwell.minutes', { defaultValue: '{{n}} min', n: Math.max(1, Math.round(hours * 60)) });
  if (hours < 48) return t('orders.dwell.hours', { defaultValue: '{{n}}h', n: Math.round(hours) });
  return t('orders.dwell.days', { defaultValue: '{{n}}d', n: Math.round(hours / 24) });
};

export const isPastSla = (order: OrderResponse): boolean => {
  const stage = stageOf(order);
  const sla = STAGE_SLA_HOURS[stage];
  const h = hoursSince(order.stage_changed_at || order.updated_at);
  return !!sla && h !== null && h > sla;
};

/** Channel chip on the order id (spec §6). */
export const channelOf = (order: OrderResponse): 'email' | 'whatsapp' | 'api' | 'manual' | 'web' | 'insurance' => {
  switch (order.source) {
    case 'email': return 'email';
    case 'whatsapp': return 'whatsapp';
    case 'api':
    case 'api_search':
    case 'b2b_api':
    case 'workflow': return 'api';
    case 'insurance_portal': return 'insurance';
    case 'guest':
    case 'website':
    case 'b2b_portal': return 'web';
    default: return 'manual';
  }
};

/** The subject line: the only way to identify an order that failed before a VIN was extracted. */
export const subjectOf = (order: OrderResponse): string | null => {
  const name = (order.name || '').trim();
  if (!name) return null;
  if (/^order-[0-9a-f]{16,}$/i.test(name)) return null; // agent-generated id, not a subject
  return name.replace(/^Email:\s*/i, '');
};

export const partsCounts = (order: OrderResponse): { requested: number; found: number; inCart: number } | null => {
  const summary: any = order.parts_summary;
  const inCart = Array.isArray(order.parts_in_order)
    ? order.parts_in_order.length
    : (order.parts_in_order?.items?.length ?? 0);
  if (!summary || !Array.isArray(summary.requested)) return inCart ? { requested: inCart, found: inCart, inCart } : null;
  const requested = Number.isInteger(summary.counts?.requested)
    ? summary.counts.requested : summary.requested.length;
  const found = Number.isInteger(summary.counts?.found)
    ? summary.counts.found
    : (Array.isArray(summary.found)
      ? summary.found.length
      : Math.max(0, requested - (Array.isArray(summary.not_found) ? summary.not_found.length : 0)));
  return { requested, found: Math.min(Math.max(0, found), requested), inCart };
};

/** Vehicle line for the list: "Make Model" + a year/code tag + the VIN tail. */
export const vehicleOf = (order: OrderResponse): { model: string | null; tag: string | null; vin: string | null; tail: string | null; plate?: string } => {
  const vd: any = order.vin_data || {};
  const make = String(vd.datasetName || vd.make || '').trim();
  const modelName = String(order.extra_data?.alt_model || vd.alt_model || vd.modelName || vd.model || '').trim();
  const model = (modelName && make && modelName.toLowerCase().startsWith(make.toLowerCase()))
    ? modelName
    : [make, modelName].filter(Boolean).join(' ') || null;
  // Chassis code when the catalog gives one, else the model year (a qualifier like
  // "May 19, 2015" is cut down to its year).
  const qualifier = String(vd.modelQualifierName || vd.year || '').trim();
  const tag = String(vd.modelCode || '').trim() || (qualifier.match(/\b(19|20)\d{2}\b/) || [])[0] || qualifier || null;
  const vin = String(order.vin || vd.vin || '').trim() || null;
  // Licence plate — only plate-lookup orgs store one (vin_data.plate).
  const plate = String(vd.plate || '').trim();
  return { model, tag, vin, tail: vin ? vin.slice(-8) : null, ...(plate ? { plate } : {}) };
};

/** The one button on the row. `strip` opens the attention strip under the row; `chat` opens the order. */
export type NextStep = { key: string; label: string; tone: 'amber' | 'orange' | 'red' | 'lilac' | 'neutral' | 'muted'; target: 'strip' | 'chat' };
export const nextStepOf = (order: OrderResponse): NextStep => {
  const answer = (target: NextStep['target']): NextStep => ({ key: 'answer', label: 'Answer question', tone: 'amber', target });
  if (order.kind === 'session') {
    if (order.processing) return { key: 'view', label: 'View', tone: 'neutral', target: 'chat' };
    return order.needs_clarification ? answer('chat') : { key: 'continue', label: 'Continue', tone: 'neutral', target: 'chat' };
  }
  const reason = order.attention_reason || '';
  switch (order.attention_flag) {
    case 'system_error': return { key: 'error', label: 'See error', tone: 'red', target: 'strip' };
    case 'needs_customer': return { key: 'reply', label: 'Reply to sender', tone: 'orange', target: 'strip' };
    case 'waiting_customer': return { key: 'waiting', label: 'Waiting on customer', tone: 'lilac', target: 'strip' };
    case 'needs_you':
      if (reason === 'assistance_requested') return { key: 'review', label: 'Review candidates', tone: 'amber', target: 'strip' };
      if (reason === 'questions_internal' || reason === 'cannot_separate' || order.needs_clarification) return answer('strip');
      return { key: 'resolve', label: 'Resolve', tone: 'amber', target: 'strip' };
  }
  if (order.needs_clarification) return answer('chat');
  const stage = stageOf(order);
  if (stage === 'in_cart') return { key: 'review_cart', label: 'Review cart', tone: 'neutral', target: 'chat' };
  if (stage === 'results_ready') return { key: 'review_results', label: 'Review results', tone: 'neutral', target: 'chat' };
  if ((stage === 'analysing' || stage === 'received') && ownerTypeOf(order) === 'agent') return { key: 'agent', label: 'Agent analysing', tone: 'muted', target: 'chat' };
  if (['submitted', 'confirmed', 'completed', 'cancelled'].includes(stage)) return { key: 'open', label: 'Open', tone: 'neutral', target: 'chat' };
  return { key: 'continue', label: 'Continue', tone: 'neutral', target: 'chat' };
};

/** Footer sort — reorders the loaded page only; the server still pages by created_at. */
export type OrderSort = 'newest' | 'needs_action' | 'sla' | 'waiting' | 'updated';
export const ORDER_SORTS: OrderSort[] = ['newest', 'needs_action', 'sla', 'waiting', 'updated'];
const dwellHours = (o: OrderResponse) => hoursSince(o.stage_changed_at || o.updated_at) ?? 0;
const slaLeft = (o: OrderResponse) => {
  const sla = o.kind === 'session' ? undefined : STAGE_SLA_HOURS[stageOf(o)];
  return sla === undefined ? Infinity : sla - dwellHours(o);
};
const urgency = (o: OrderResponse) =>
  o.needs_clarification || (o.kind !== 'session' && ((!!o.attention_flag && NEEDS_ACTION_FLAGS.includes(o.attention_flag)) || isUnowned(o))) ? 0 : 1;
export const sortOrders = (rows: OrderResponse[], key: OrderSort): OrderResponse[] => [...rows].sort((a, b) => {
  switch (key) {
    case 'newest': return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    case 'needs_action': return (urgency(a) - urgency(b)) || (dwellHours(b) - dwellHours(a));
    case 'sla': return slaLeft(a) - slaLeft(b);
    case 'waiting': return dwellHours(b) - dwellHours(a);
    default: return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime();
  }
});
